import React from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy, lazyForPaint } from '@atlassian/react-loosely-lazy';
import { fg } from '@atlassian/jira-feature-gating';
import type { CookieConsentBanner } from './banner/CookieConsentBanner.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyCookieConsentBanner = lazy<typeof CookieConsentBanner>(
	() =>
		// chunk name is async-ccb to avoid scripts with cookie/banner keywords getting blocked
		import(/* webpackChunkName: "async-ccb" */ './banner/CookieConsentBanner.tsx').then(
			({ CookieConsentBanner }) => CookieConsentBanner,
		),
	{ entryFsSsrLiftImportToModule: false },
);

const LazyForPaintCookieConsentBanner = lazyForPaint<typeof CookieConsentBanner>(
	() =>
		// chunk name is async-cc-banner to avoid scripts with cookie/banner keywords getting blocked
		import(/* webpackChunkName: "async-cc-banner" */ './banner/CookieConsentBanner.tsx').then(
			({ CookieConsentBanner }) => CookieConsentBanner,
		),
	{
		ssr: true,
		entryFsSsrLiftImportToModule: true,
		// eslint-disable-next-line jira/entry-fs/restrict-shell-only-to-navigation-apps
		includeInShellOnlySsrBundle: true,
		entryFsSsrOverride: 'include',
	},
);

export const CookieConsentBannerAsync = () => (
	<Box xcss={boxStyles}>
		<JSErrorBoundary
			id="async-cookie-consent-banner"
			packageName="jiraCookieConsentBanner"
			teamName="Magma"
		>
			<Placeholder name="async-cookie-consent-banner" fallback={null}>
				{fg('jfp-magma-ssr-iv-cookie-consent-banner') ? (
					<LazyForPaintCookieConsentBanner />
				) : (
					<LazyCookieConsentBanner />
				)}
			</Placeholder>
		</JSErrorBoundary>
	</Box>
);

const boxStyles = xcss({
	backgroundColor: 'elevation.surface.sunken',
	height: '100%',
});
